import { UseFormRegister, UseFormWatch, Control, FieldErrors } from 'react-hook-form';
import { CheckboxField } from '@/components/common/form-fields';
import { Toggle } from '@/components/ui/Toggle';
import { Users, Ticket } from 'lucide-react';

interface OrganizationalNotificationsSectionProps {
  register: UseFormRegister<any>;
  watch: UseFormWatch<any>;
  control: Control<any>;
  errors: FieldErrors<any>;
}

export default function OrganizationalNotificationsSection({
  register,
  watch,
  control,
  errors,
}: OrganizationalNotificationsSectionProps) {
  const enableOrganizationalNotifications = watch('enable_organizational_email_notifications');
  const enableSelfTicketAssignment = watch('enable_self_ticket_assignment');

  return (
    <div className="space-y-6">
      {/* Organizational Hierarchy Email Notifications */}
      <div className="card">
        <div className="flex items-center gap-2 mb-6">
          <Users className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold">Organization Email Notifications</h2>
        </div>
        <div className="space-y-6">
          {/* Master Toggle */}
          <Toggle
            name="enable_organizational_email_notifications"
            control={control}
            label="Enable Organizational Email Notifications"
            description="Enable email notifications to organizational hierarchy members (BU Head, Reporting Manager, HRBP)"
          />

          {/* Conditional checkboxes - shown only when master toggle is enabled */}
          {enableOrganizationalNotifications && (
            <div className="ml-6 pl-4 border-l-2 border-blue-200 space-y-4">
              <CheckboxField
                id="notify_bu_head"
                checkboxLabel="Notify BU Head"
                checkboxDescription="Send email notifications to the Business Unit Head"
                {...register('notify_bu_head')}
              />

              <CheckboxField
                id="notify_reporting_manager"
                checkboxLabel="Notify Reporting Manager"
                checkboxDescription="Send email notifications to the Reporting Manager"
                {...register('notify_reporting_manager')}
              />

              <CheckboxField
                id="notify_hrbp"
                checkboxLabel="Notify HRBP"
                checkboxDescription="Send email notifications to the HR Business Partner"
                {...register('notify_hrbp')}
              />

              {errors.notify_hrbp && (
                <p className="text-red-500 text-sm mt-2">{errors.notify_hrbp.message as string}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Self Ticket Assignment Notifications */}
      <div className="card">
        <div className="flex items-center gap-2 mb-6">
          <Ticket className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold">Organization Ticket Notification</h2>
        </div>
        <div className="space-y-6">
          {/* Master Toggle */}
          <Toggle
            name="enable_self_ticket_assignment"
            control={control}
            label="Enable Self Ticket Assignment"
            description="Allow users to self-assign tickets and configure approvers"
          />

          {/* Conditional checkboxes - shown only when master toggle is enabled */}
          {enableSelfTicketAssignment && (
            <div className="ml-6 pl-4 border-l-2 border-blue-200 space-y-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Self Ticket Approvers</p>

              <CheckboxField
                id="self_ticket_approvers.bu_head"
                checkboxLabel="BU Head"
                checkboxDescription="Allow BU Head to approve self-assigned tickets"
                {...register('self_ticket_approvers.bu_head')}
              />

              <CheckboxField
                id="self_ticket_approvers.reporting_manager"
                checkboxLabel="Reporting Manager"
                checkboxDescription="Allow Reporting Manager to approve self-assigned tickets"
                {...register('self_ticket_approvers.reporting_manager')}
              />

              <CheckboxField
                id="self_ticket_approvers.hrbp"
                checkboxLabel="HRBP"
                checkboxDescription="Allow HR Business Partner to approve self-assigned tickets"
                {...register('self_ticket_approvers.hrbp')}
              />

              <CheckboxField
                id="self_ticket_approvers.all_admins"
                checkboxLabel="All Admins"
                checkboxDescription="Allow all administrators to approve self-assigned tickets"
                {...register('self_ticket_approvers.all_admins')}
              />

              {(errors.self_ticket_approvers as any)?.all_admins && (
                <p className="text-red-500 text-sm mt-2">{(errors.self_ticket_approvers as any)?.all_admins?.message}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
